import { z } from 'zod';
import type { Day, Manifest } from '../content/data';

export const PROGRESS_STORAGE_KEY = 'frontend-fieldbook:progress:v1';

const questionAttemptSchema = z.object({
  attempted: z.boolean(),
  notes: z.string().max(10_000)
});

const learnerProfileSchema = z.object({
  name: z.string().max(80),
  targetRole: z.string().max(100)
});

const emptyProfile = {
  name: '',
  targetRole: 'Frontend developer'
} as const;

const progressSchema = z.object({
  version: z.literal(1),
  onboardingComplete: z.boolean(),
  startedAt: z.string().datetime().nullable(),
  updatedAt: z.string().datetime().nullable(),
  completedDayIds: z.array(z.string().regex(/^week-\d{2}-day-\d{2}$/)),
  completedAtByDay: z.record(
    z.string().regex(/^week-\d{2}-day-\d{2}$/),
    z.string().datetime()
  ).default({}),
  profile: learnerProfileSchema.default(emptyProfile),
  stageByDay: z.record(z.string(), z.number().int().min(0).max(3)),
  checklist: z.record(z.string(), z.boolean()),
  questionAttempts: z.record(z.string(), questionAttemptSchema),
  reflections: z.record(z.string(), z.string().max(20_000))
});

export type Progress = z.infer<typeof progressSchema>;
export type QuestionAttempt = z.infer<typeof questionAttemptSchema>;
export type LearnerProfile = z.infer<typeof learnerProfileSchema>;

export function createEmptyProgress(): Progress {
  return {
    version: 1,
    onboardingComplete: false,
    startedAt: null,
    updatedAt: null,
    completedDayIds: [],
    completedAtByDay: {},
    profile: { ...emptyProfile },
    stageByDay: {},
    checklist: {},
    questionAttempts: {},
    reflections: {}
  };
}

export function parseStoredProgress(raw: string | null): Progress {
  if (!raw) return createEmptyProgress();
  try {
    return progressSchema.parse(JSON.parse(raw));
  } catch {
    return createEmptyProgress();
  }
}

export function loadStoredProgress(): Progress {
  try {
    return parseStoredProgress(globalThis.localStorage?.getItem(PROGRESS_STORAGE_KEY) ?? null);
  } catch {
    return createEmptyProgress();
  }
}

export function saveStoredProgress(progress: Progress): boolean {
  try {
    globalThis.localStorage?.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
    return true;
  } catch {
    return false;
  }
}

export function allDayIds(manifest: Manifest): string[] {
  return manifest.weeks.flatMap((week) =>
    Array.from({ length: 7 }, (_, index) => `${week.id}-day-${String(index + 1).padStart(2, '0')}`)
  );
}

export function firstIncompleteDayId(manifest: Manifest, progress: Progress): string | null {
  const completed = new Set(progress.completedDayIds);
  return allDayIds(manifest).find((dayId) => !completed.has(dayId)) ?? null;
}

export function completedDayCount(manifest: Manifest, progress: Progress): number {
  const validIds = new Set(allDayIds(manifest));
  return new Set(progress.completedDayIds.filter((dayId) => validIds.has(dayId))).size;
}

export function isDayUnlocked(manifest: Manifest, progress: Progress, dayId: string): boolean {
  const dayIds = allDayIds(manifest);
  const requestedIndex = dayIds.indexOf(dayId);
  const currentDayId = firstIncompleteDayId(manifest, progress);
  const currentIndex = currentDayId ? dayIds.indexOf(currentDayId) : dayIds.length;
  return requestedIndex >= 0 && requestedIndex <= currentIndex;
}

export function requiredReflectionIds(day: Day): string[] {
  const patterns = day.kind === 'sunday'
    ? [/what can i now build/i, /where did time exceed/i, /first 20-minute action/i]
    : [/what did i finish/i, /what still feels unclear/i, /first 20-minute action/i];
  const matches = day.reflectionFields.filter((field) => patterns.some((pattern) => pattern.test(field.label)));
  return matches.length > 0 ? matches.map((field) => field.id) : day.reflectionFields.slice(0, 2).map((field) => field.id);
}

export type CompletionStatus = {
  ready: boolean;
  checklistDone: number;
  checklistTotal: number;
  questionsDone: number;
  questionsTotal: number;
  reflectionsDone: number;
  reflectionsTotal: number;
};

export function dayCompletionStatus(day: Day, progress: Progress): CompletionStatus {
  const requiredReflections = requiredReflectionIds(day);
  const checklistDone = day.completionChecklistIds.filter((id) => progress.checklist[id]).length;
  const questionsDone = day.questionIds.filter((id) => progress.questionAttempts[id]?.attempted).length;
  const reflectionsDone = requiredReflections.filter((id) => progress.reflections[id]?.trim()).length;

  return {
    ready:
      checklistDone === day.completionChecklistIds.length &&
      questionsDone === day.questionIds.length &&
      reflectionsDone === requiredReflections.length,
    checklistDone,
    checklistTotal: day.completionChecklistIds.length,
    questionsDone,
    questionsTotal: day.questionIds.length,
    reflectionsDone,
    reflectionsTotal: requiredReflections.length
  };
}

export function parseProgressImport(text: string): Progress {
  return progressSchema.parse(JSON.parse(text));
}

export function serializeProgress(progress: Progress): string {
  return `${JSON.stringify(progress, null, 2)}\n`;
}
